import { useEffect, useState } from "react";
import DocumentMeta from "react-document-meta";
import ExampleLandingHeader from "../components/ExampleLandingHeader";
import { useTranslation } from "../i18n/LanguageContext";
import FloatingWhatsAppButton from "../components/FloatingWhatsAppButton";
import SocialLinksSection from "../components/SocialLinksSection";
import { buildNoIndexMeta } from "../seo";

const copy = {
    it: {
        title: "Resort Cala Dorata",
        metaTitle: "Resort Cala Dorata | Vacanza in Sardegna",
        metaDescription: "Camere vista mare, spa e colazione inclusa. Richiedi la tua disponibilità in pochi secondi.",
        intro: "Camere luminose, spiaggia privata e tramonti indimenticabili. La tua estate in Sardegna inizia qui.",
        menu: [
            { label: "Camere", href: "#camere" },
            { label: "Servizi", href: "#servizi" },
            { label: "Contatto", href: "#contatto" }
        ],
        ctaPrimary: "Scopri le camere",
        ctaSecondary: "Richiedi disponibilità",
        packageLabel: "Pacchetto estate",
        packagePrice: "da €180 / notte",
        packageNote: "Colazione e spa incluse",
        heroAlt: "Resort al tramonto con vista mare",
        services: [
            { title: "Camere", body: "Vista mare, suite famiglia, junior suite." },
            { title: "Benessere", body: "Spa, massaggi e area relax." },
            { title: "Servizi", body: "Navetta spiaggia, colazione, concierge." }
        ],
        roomsTitle: "Le nostre camere",
        rooms: [
            { title: "Vista mare", body: "Balcone privato e colazione inclusa." },
            { title: "Suite famiglia", body: "Spazi ampi e area relax dedicata." },
            { title: "Junior suite", body: "Perfetta per coppie e weekend lunghi." }
        ],
        camereGalleryAlts: [
            "Camera elegante con vista mare",
            "Suite luminosa con dettagli naturali",
            "Camera con balcone privato",
            "Letto matrimoniale con vista",
            "Suite con angolo relax"
        ],
        wellnessTitle: "Benessere",
        wellnessBody: "Spa, massaggi e area relax con vista giardino.",
        wellnessGalleryAlts: [
            "Spa con luci soffuse",
            "Area relax con lettini",
            "Trattamenti benessere",
            "Piscina interna riscaldata",
            "Percorso wellness"
        ],
        servicesTitle: "Servizi",
        servicesBody: "Colazione vista mare, concierge e navetta privata.",
        servicesGalleryAlts: [
            "Colazione panoramica",
            "Navetta verso la spiaggia",
            "Servizio concierge",
            "Aperitivo al tramonto",
            "Sala ristorante luminosa"
        ],
        pricesTitle: "Prezzi",
        pricesSubtitle: "Tariffe indicative in alta stagione.",
        prices: [
            { title: "Camera vista mare", body: "Da €180 a notte" },
            { title: "Suite famiglia", body: "Da €240 a notte" },
            { title: "Junior suite", body: "Da €210 a notte" }
        ],
        contactTitle: "Richiedi disponibilità",
        contactSubtitle: "Risposta veloce con offerte personalizzate.",
        contactCta1: "Contattaci",
        contactCta2: "Chiama ora"
    },
    en: {
        title: "Cala Dorata Resort",
        metaTitle: "Cala Dorata Resort | Holiday in Sardinia",
        metaDescription: "Sea-view rooms, spa, and breakfast included. Request availability in seconds.",
        intro: "Bright rooms, private beach and unforgettable sunsets. Your summer in Sardinia starts here.",
        menu: [
            { label: "Rooms", href: "#camere" },
            { label: "Services", href: "#servizi" },
            { label: "Contact", href: "#contatto" }
        ],
        ctaPrimary: "See rooms",
        ctaSecondary: "Check availability",
        packageLabel: "Summer package",
        packagePrice: "from €180 / night",
        packageNote: "Breakfast and spa included",
        heroAlt: "Sunset resort with sea view",
        services: [
            { title: "Rooms", body: "Sea view, family suites, junior suites." },
            { title: "Wellness", body: "Spa, massages and relax area." },
            { title: "Services", body: "Beach shuttle, breakfast, concierge." }
        ],
        roomsTitle: "Our rooms",
        rooms: [
            { title: "Sea view", body: "Private balcony and breakfast included." },
            { title: "Family suite", body: "Large spaces and dedicated relax area." },
            { title: "Junior suite", body: "Perfect for couples and long weekends." }
        ],
        camereGalleryAlts: [
            "Elegant room with sea view",
            "Bright suite with natural details",
            "Room with private balcony",
            "Double bed with a view",
            "Suite with a relax corner"
        ],
        wellnessTitle: "Wellness",
        wellnessBody: "Spa, massages and a relax area overlooking the garden.",
        wellnessGalleryAlts: [
            "Soft-lit spa",
            "Relax area with loungers",
            "Wellness treatments",
            "Heated indoor pool",
            "Wellness path"
        ],
        servicesTitle: "Services",
        servicesBody: "Sea-view breakfast, concierge and private shuttle.",
        servicesGalleryAlts: [
            "Panoramic breakfast",
            "Shuttle to the beach",
            "Concierge service",
            "Sunset aperitif",
            "Bright restaurant room"
        ],
        pricesTitle: "Prices",
        pricesSubtitle: "Indicative high-season rates.",
        prices: [
            { title: "Sea view room", body: "From €180 per night" },
            { title: "Family suite", body: "From €240 per night" },
            { title: "Junior suite", body: "From €210 per night" }
        ],
        contactTitle: "Check availability",
        contactSubtitle: "Quick reply with tailored offers.",
        contactCta1: "Contact us",
        contactCta2: "Call now"
    },
    de: {
        title: "Resort Cala Dorata",
        metaTitle: "Resort Cala Dorata | Urlaub in Sardinien",
        metaDescription: "Zimmer mit Meerblick, Spa und Frühstück inklusive. Verfügbarkeit in Sekunden anfragen.",
        intro: "Helle Zimmer, Privatstrand und unvergessliche Sonnenuntergänge. Ihr Sommer in Sardinien beginnt hier.",
        menu: [
            { label: "Zimmer", href: "#camere" },
            { label: "Services", href: "#servizi" },
            { label: "Kontakt", href: "#contatto" }
        ],
        ctaPrimary: "Zimmer ansehen",
        ctaSecondary: "Verfügbarkeit prüfen",
        packageLabel: "Sommerpaket",
        packagePrice: "ab €180 / Nacht",
        packageNote: "Frühstück und Spa inklusive",
        heroAlt: "Resort bei Sonnenuntergang mit Meerblick",
        services: [
            { title: "Zimmer", body: "Meerblick, Familien‑Suites, Junior‑Suites." },
            { title: "Wellness", body: "Spa, Massagen und Relax‑Bereich." },
            { title: "Services", body: "Shuttle zum Strand, Frühstück, Concierge." }
        ],
        roomsTitle: "Unsere Zimmer",
        rooms: [
            { title: "Meerblick", body: "Privater Balkon und Frühstück inklusive." },
            { title: "Familien‑Suite", body: "Viel Platz und Relax‑Bereich." },
            { title: "Junior‑Suite", body: "Ideal für Paare und lange Wochenenden." }
        ],
        camereGalleryAlts: [
            "Elegantes Zimmer mit Meerblick",
            "Helle Suite mit natürlichen Details",
            "Zimmer mit privatem Balkon",
            "Doppelbett mit Aussicht",
            "Suite mit Relax‑Ecke"
        ],
        wellnessTitle: "Wellness",
        wellnessBody: "Spa, Massagen und ein Relax‑Bereich mit Gartenblick.",
        wellnessGalleryAlts: [
            "Spa mit weichem Licht",
            "Relax‑Bereich mit Liegen",
            "Wellness‑Behandlungen",
            "Beheiztes Indoor‑Becken",
            "Wellness‑Parcours"
        ],
        servicesTitle: "Services",
        servicesBody: "Frühstück mit Meerblick, Concierge und privater Shuttle.",
        servicesGalleryAlts: [
            "Panoramafrühstück",
            "Shuttle zum Strand",
            "Concierge‑Service",
            "Aperitif bei Sonnenuntergang",
            "Heller Restaurant‑Saal"
        ],
        pricesTitle: "Preise",
        pricesSubtitle: "Unverbindliche Hochsaison‑Tarife.",
        prices: [
            { title: "Zimmer mit Meerblick", body: "Ab €180 pro Nacht" },
            { title: "Familien‑Suite", body: "Ab €240 pro Nacht" },
            { title: "Junior‑Suite", body: "Ab €210 pro Nacht" }
        ],
        contactTitle: "Verfügbarkeit anfragen",
        contactSubtitle: "Schnelle Antwort mit individuellen Angeboten.",
        contactCta1: "Kontakt",
        contactCta2: "Jetzt anrufen"
    }
};

const camereGalleryImages = [
    "https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1501117716987-c8e1ecb210e1?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1445019980597-93fa8acb246c?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1200&auto=format&fit=crop&sat=-30"
];

const wellnessGalleryImages = [
    "https://images.unsplash.com/photo-1540555700478-4be289fbecef?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1501117716987-c8e1ecb210e1?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1501004318641-b39e6451bec6?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?q=80&w=1200&auto=format&fit=crop&sat=-20"
];

const servicesGalleryImages = [
    "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1200&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1528605248644-14dd04022da1?q=80&w=1200&auto=format&fit=crop"
];

const fallbackImage = "https://images.unsplash.com/photo-1500375592092-40eb2168fd21?q=80&w=1200&auto=format&fit=crop";

const SafeImage = ({ src, alt, className }) => (
    <img
        src={src}
        alt={alt}
        className={className}
        loading="lazy"
        onError={(event) => {
            if (event.currentTarget.src !== fallbackImage) {
                event.currentTarget.src = fallbackImage;
            }
        }}
    />
);

const AutoCarousel = ({ images, alts }) => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const id = setInterval(() => {
            setIndex((prev) => (prev + 1) % images.length);
        }, 3500);
        return () => clearInterval(id);
    }, [images.length]);

    return (
        <div className="relative h-40 overflow-hidden rounded-2xl md:h-44">
            {images.map((src, idx) => (
                <SafeImage
                    key={src}
                    src={src}
                    alt={alts[idx]}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${idx === index ? "opacity-100" : "opacity-0"}`}
                />
            ))}
        </div>
    );
};

const ResortPage = () => {
    const { language } = useTranslation();
    const t = copy[language] || copy.it;

    const meta = buildNoIndexMeta({
        title: t.metaTitle,
        description: t.metaDescription,
        path: "/estate-2026/resort"
    });

    return (
        <>
            <DocumentMeta {...meta} />
            <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-stone-100 text-stone-900">
                <header className="px-6 pt-10 pb-8 md:px-12 lg:px-20">
                    <ExampleLandingHeader title={t.title} items={t.menu} />
                    <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_0.8fr]">
                        <div>
                            <p className="text-lg text-stone-600">{t.intro}</p>
                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <a href="#camere" className="rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-stone-300">
                                    {t.ctaPrimary}
                                </a>
                                <a href="#contatto" className="rounded-full border border-stone-900 px-6 py-3 text-sm font-semibold text-stone-900">
                                    {t.ctaSecondary}
                                </a>
                            </div>
                        </div>
                        <div className="rounded-3xl bg-stone-900 p-6 text-white shadow-lg">
                            <p className="text-sm uppercase tracking-widest text-stone-300">{t.packageLabel}</p>
                            <p className="mt-2 text-3xl font-semibold">{t.packagePrice}</p>
                            <p className="mt-2 text-sm text-stone-300">{t.packageNote}</p>
                        </div>
                    </div>
                    <div className="mt-8 overflow-hidden rounded-3xl shadow-lg">
                        <SafeImage
                            src="https://images.unsplash.com/photo-1500375592092-40eb2168fd21?q=80&w=1600&auto=format&fit=crop"
                            alt={t.heroAlt}
                            className="h-64 w-full object-cover md:h-96"
                        />
                    </div>
                </header>

                <main className="px-6 pb-24 md:px-12 lg:px-20">
                    <section id="servizi" className="grid gap-6 lg:grid-cols-3">
                        {t.services.map((item) => (
                            <div key={item.title} className="rounded-3xl bg-white p-6 shadow-lg">
                                <h2 className="text-xl font-semibold">{item.title}</h2>
                                <p className="mt-2 text-sm text-stone-600">{item.body}</p>
                            </div>
                        ))}
                    </section>
                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                        <img
                            src="https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1400&auto=format&fit=crop"
                            alt="Camera elegante in resort"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                        <img
                            src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1400&auto=format&fit=crop"
                            alt="Spiaggia privata"
                            className="h-56 w-full rounded-3xl object-cover md:h-72"
                            loading="lazy"
                        />
                    </div>

                    <section id="camere" className="mt-10 rounded-3xl bg-white/95 p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.roomsTitle}</h2>
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            {t.rooms.map((item) => (
                                <div key={item.title} className="rounded-2xl bg-stone-50 p-4">
                                    <p className="font-semibold">{item.title}</p>
                                    <p className="mt-2 text-sm text-stone-600">{item.body}</p>
                                </div>
                            ))}
                        </div>
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            <AutoCarousel images={camereGalleryImages} alts={t.camereGalleryAlts} />
                            <AutoCarousel images={camereGalleryImages.slice(1)} alts={t.camereGalleryAlts.slice(1)} />
                            <AutoCarousel images={camereGalleryImages.slice(2)} alts={t.camereGalleryAlts.slice(2)} />
                        </div>
                    </section>
                    <section className="mt-10 grid gap-6 lg:grid-cols-[1fr_1fr]">
                        <div className="rounded-3xl bg-white p-6 shadow-lg">
                            <h2 className="text-2xl font-semibold">{t.wellnessTitle}</h2>
                            <p className="mt-2 text-sm text-stone-600">{t.wellnessBody}</p>
                            <div className="mt-6 grid gap-4 md:grid-cols-3">
                                <AutoCarousel images={wellnessGalleryImages} alts={t.wellnessGalleryAlts} />
                                <AutoCarousel images={wellnessGalleryImages.slice(1)} alts={t.wellnessGalleryAlts.slice(1)} />
                                <AutoCarousel images={wellnessGalleryImages.slice(2)} alts={t.wellnessGalleryAlts.slice(2)} />
                            </div>
                        </div>
                        <div className="rounded-3xl bg-white p-6 shadow-lg">
                            <h2 className="text-2xl font-semibold">{t.servicesTitle}</h2>
                            <p className="mt-2 text-sm text-stone-600">{t.servicesBody}</p>
                            <div className="mt-6 grid gap-4 md:grid-cols-3">
                                <AutoCarousel images={servicesGalleryImages} alts={t.servicesGalleryAlts} />
                                <AutoCarousel images={servicesGalleryImages.slice(1)} alts={t.servicesGalleryAlts.slice(1)} />
                                <AutoCarousel images={servicesGalleryImages.slice(2)} alts={t.servicesGalleryAlts.slice(2)} />
                            </div>
                        </div>
                    </section>
                    <section className="mt-10 rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <h2 className="text-2xl font-semibold">{t.pricesTitle}</h2>
                        <p className="mt-2 text-sm text-stone-600">{t.pricesSubtitle}</p>
                        <div className="mt-6 grid gap-4 md:grid-cols-3">
                            {t.prices.map((item) => (
                                <div key={item.title} className="rounded-2xl border border-stone-200 p-4">
                                    <p className="font-semibold">{item.title}</p>
                                    <p className="mt-2 text-sm text-stone-600">{item.body}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    <SocialLinksSection className="mt-10" />

                    <section id="contatto" className="mt-10 rounded-3xl bg-white p-6 shadow-lg md:p-10">
                        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                            <div>
                                <h2 className="text-2xl font-semibold">{t.contactTitle}</h2>
                                <p className="mt-2 text-stone-600">{t.contactSubtitle}</p>
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row">
                                <a href="/estate-2026#contatto" className="rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white">
                                    {t.contactCta1}
                                </a>
                                <a href="tel:+393515699197" className="rounded-full border border-stone-900 px-6 py-3 text-sm font-semibold text-stone-900">
                                    {t.contactCta2}
                                </a>
                            </div>
                        </div>
                    </section>
                </main>
                <FloatingWhatsAppButton />
            </div>
        </>
    );
};

export default ResortPage;
